import React, { useMemo, useCallback, useEffect, useRef } from 'react'
import type { ChallengeService } from '@duck/api-definitions'
import { ChallengesTable } from './ChallengesTable'
import { baseColumns } from './adminChallenges.utils'
import { useTar } from '@/modules'
import { useAdminChallenges } from '@/hooks/useAdminChallenges'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useTableSettings } from '@/hooks/TableSettings/useTableSettings'
import { type MultiPickOption, Breadcrumbs } from '@duck/ui'
import { fetchUserOptions } from '../DataInputs/UserDataInput'
import { fetchTaskOptions } from '../DataInputs/TaskDataInput'
import { Link } from '@tanstack/react-router'

const TABLE_ID = 'admin-challenges'

const defaultVisibleColumns = Object.fromEntries(
  baseColumns.map((col) => [col.key, col.visible])
) as Record<string, boolean>

/** Filter keys match backend queryFilters: status, feedbackStatus, userId, taskId (assignmentIds not in UI yet). */
const FILTER_KEYS = { userId: 'userId', taskId: 'taskId', status: 'status', feedbackStatus: 'feedbackStatus', slug: 'slug', weaponOfChoice: 'weaponOfChoice' } as const

export const ChallengeManagement: React.FC = () => {
  const { t } = useTar()
  useDocumentTitle('Challenges', 'admin')

  const {
    sortColumn,
    sortDirection,
    toggleSort,
    isColumnVisible,
    toggleColumnVisibility,
    getColumnFilter,
    setColumnFilter,
    getColumnFilterArray,
    setColumnFilterArray,
    toggleColumnFilterArrayItem,
    clearColumnFilter,
    clearAllSettings,
    hasActiveSettings,
    filters,
    visibleColumns: visibleColumnsRecord,
    filterLabels,
    setFilterLabel,
    applyFilterSet,
    setSort,
  } = useTableSettings({
    tableId: TABLE_ID,
    defaultVisibleColumns,
    defaultSort: { columnKey: 'startTime', direction: 'desc' },
  })

  const sortField = (sortColumn ?? 'startTime') as keyof ChallengeService.Challenge
  const sortDirectionResolved = sortDirection === 'asc' || sortDirection === 'desc' ? sortDirection : 'desc'

  const visibleColumns = useMemo(
    () => baseColumns.filter((col) => isColumnVisible(col.key)),
    [isColumnVisible]
  )

  const userIdFilterArr = getColumnFilterArray(FILTER_KEYS.userId)
  const taskIdFilterArr = getColumnFilterArray(FILTER_KEYS.taskId)
  const statusFilterArr = getColumnFilterArray(FILTER_KEYS.status)
  const feedbackStatusFilterArr = getColumnFilterArray(FILTER_KEYS.feedbackStatus)
  const weaponOfChoiceFilterArr = getColumnFilterArray(FILTER_KEYS.weaponOfChoice)
  const slugFilter = getColumnFilter(FILTER_KEYS.slug)

  // Backend filter arrays: match any value in each array (status, feedbackStatus, userId, taskId, assignmentIds)
  const queryFilters = useMemo(
    () => ({
      status: statusFilterArr.length > 0 ? (statusFilterArr as ChallengeService.ChallengeStatusEnum[]) : undefined,
      feedbackStatus:
        feedbackStatusFilterArr.length > 0
          ? (feedbackStatusFilterArr as ChallengeService.FeedbackStatusFilterOption[])
          : undefined,
      userId: userIdFilterArr.length > 0 ? userIdFilterArr : undefined,
      taskId: taskIdFilterArr.length > 0 ? taskIdFilterArr : undefined,
      slug: slugFilter?.trim() || undefined,
      weaponOfChoice:
        weaponOfChoiceFilterArr.length > 0
          ? (weaponOfChoiceFilterArr as ChallengeService.TaskWeaponsOfChoiceEnum[])
          : undefined,
    }),
    [statusFilterArr, feedbackStatusFilterArr, userIdFilterArr, taskIdFilterArr, slugFilter, weaponOfChoiceFilterArr]
  )

  // Cache resolved option labels so the FilterSetManager can display names instead of IDs
  const lastFetchedOptionsRef = useRef<Record<string, MultiPickOption[]>>({})

  const fetchUserOptionsWithCache = useCallback(async (query?: string): Promise<MultiPickOption[]> => {
    const options = await fetchUserOptions(query, 20)
    const existing = lastFetchedOptionsRef.current[FILTER_KEYS.userId] ?? []
    const merged = new Map(existing.map(o => [o.value, o]))
    for (const o of options) merged.set(o.value, o)
    lastFetchedOptionsRef.current[FILTER_KEYS.userId] = Array.from(merged.values())
    return options
  }, [])

  const fetchTaskOptionsWithCache = useCallback(async (): Promise<MultiPickOption[]> => {
    const options = await fetchTaskOptions()
    lastFetchedOptionsRef.current[FILTER_KEYS.taskId] = options
    return options
  }, [])

  const asyncSearchFetchers = useMemo(
    () => ({
      userId: fetchUserOptionsWithCache,
      taskId: fetchTaskOptionsWithCache,
    }),
    [fetchUserOptionsWithCache, fetchTaskOptionsWithCache],
  )

  // Cache labels for currently-selected async-search filter values
  useEffect(() => {
    const asyncKeys = [FILTER_KEYS.userId, FILTER_KEYS.taskId] as const
    for (const key of asyncKeys) {
      const selectedIds = key === FILTER_KEYS.userId ? userIdFilterArr : taskIdFilterArr
      const fetchedOptions = lastFetchedOptionsRef.current[key] ?? []
      for (const id of selectedIds) {
        if (filterLabels[key]?.[id]) continue
        const opt = fetchedOptions.find(o => o.value === id)
        if (opt) setFilterLabel(key, opt.value, opt.label)
      }
    }
  }, [userIdFilterArr, taskIdFilterArr, filterLabels, setFilterLabel])

  const {
    challenges,
    hasMore,
    isInitialLoading,
    isFetchingNextPage,
    isError,
    fetchNextPage,
    refetch,
  } = useAdminChallenges({
    queryFilters,
    sortField,
    sortDirection: sortDirectionResolved,
  })
  const handleLoadMorePage = useCallback(() => {
    if (!hasMore) return
    void fetchNextPage()
  }, [hasMore, fetchNextPage])

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('challenges.breadcrumbAdmin'), href: '/admin' },
          { label: t('challenges.breadcrumbChallenges') },
        ]}
        renderLink={(item, children) => <Link to={item.href}>{children}</Link>}
      />
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
            {t('challenges.challengeManagement')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('challenges.challengeManagementDescription')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { void refetch() }}
            className="px-4 py-2 bg-secondary text-foreground rounded-md text-sm font-medium hover:bg-secondary/80 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {/* Error State */}
      {isError && (
        <div className="rounded-md bg-error/10 border border-error/20 p-4">
          <div className="text-error text-sm">
            {t('common.error')}
          </div>
        </div>
      )}

      {/* Challenges Table */}
      {!isError && (
        <ChallengesTable
          challenges={challenges}
          totalChallenges={challenges.length}
          visibleColumns={visibleColumns}
          sortField={sortField}
          sortDirection={sortDirectionResolved}
          onSort={toggleSort}
          loadingMode={isInitialLoading}
          hasMore={hasMore}
          onLoadMore={handleLoadMorePage}
          isPageLoading={isFetchingNextPage}
          filterApi={{
            getColumnFilter,
            setColumnFilter,
            getColumnFilterArray,
            setColumnFilterArray,
            toggleColumnFilterArrayItem,
            clearColumnFilter,
          }}
          asyncSearchFetchers={asyncSearchFetchers}
          hasNoFilterResults={hasActiveSettings() && challenges.length === 0 && !isInitialLoading}
          onClearFilters={clearAllSettings}
          tableSettings={{
            hasActiveSettings,
            clearAllSettings,
            isColumnVisible,
            toggleColumnVisibility,
            availableColumns: baseColumns.map((col) => ({ key: col.key, label: t(col.label) })),
            tableId: TABLE_ID,
            sortColumn,
            sortDirection,
            filters,
            visibleColumns: visibleColumnsRecord,
            defaultVisibleColumns,
            filterLabels,
            setSort,
            clearColumnFilter,
            applyFilterSet,
          }}
        />
      )}
    </div>
  )
}
